import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Checkbox,
  Col,
  ControlLabel,
  Dropdown,
  DropdownItem,
  Grid,
  Icon,
  NetworkIcon,
  Popover,
  TextInput,
  Tooltip,
  Typography,
} from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import ActionButtons from 'components/Filters/ActionButtons'
import networkIconNamesMap from 'modules/Statistics/networkIconNamesMap'
import PropTypes from 'prop-types'
import styles from 'modules/Statistics/statistics.module.scss'
import { useSelector } from 'react-redux'

const GroupManager = ({
  ButtonComponent = () => (
    <Icon
      name="settings"
      size="m"
      color={'Interface-Seventh'}
    />
  ),
  id,
  name = '',
  selectedAccountIds = [],
  onApply = () => {},
  onReset = () => {},
  isDemoMode = false,
}) => {
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const { t } = useTranslation()
  const accounts = useSelector((state) => state['statistics'].accounts) ?? []
  const initialSortDoneRef = useRef(false)

  const [localAccounts, setLocalAccounts] = useState(accounts)

  useEffect(() => {
    setLocalAccounts(accounts)
  }, [accounts])

  const popoverState = usePopupState({
    variant: 'popover',
    id: 'groupManagerPopover',
    breakpoints: {
      0: {
        variant: 'bottomSheet',
      },
      600: {
        variant: 'popover',
      },
    },
  })

  const [draftGroupName, setDraftGroupName] = useState(name ?? '')
  const [draftSelectedIds, setDraftSelectedIds] = useState(selectedAccountIds ?? [])

  useEffect(() => setDraftGroupName(name ?? ''), [name])
  useEffect(() => setDraftSelectedIds(selectedAccountIds ?? []), [selectedAccountIds])

  const normalizedAccounts = useMemo(
    () =>
      localAccounts.map((item) => {
        const account = item?.account ?? item

        return {
          id: account?.id ?? item?.id,
          login: account?.login ?? item?.login ?? '',
          network: account?.network ?? item?.network,
        }
      }),
    [localAccounts],
  )

  const toggleAccount = useCallback((accountId) => {
    setDraftSelectedIds((prev) => {
      if (!accountId && accountId !== 0) return prev
      return prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : prev.concat(accountId)
    })
  }, [])

  const dropSelection = useCallback(() => {
    setDraftGroupName(name ?? '')
    setDraftSelectedIds(selectedAccountIds ?? [])
    onReset()
  }, [name, selectedAccountIds, onReset])

  useEffect(() => {
    if (initialSortDoneRef.current) return
    if (!draftSelectedIds.length && !accounts.length) return
    initialSortDoneRef.current = true

    const selected = new window.Set(draftSelectedIds)

    setLocalAccounts(
      [...localAccounts].sort((a, b) => {
        const aSelected = selected.has(String(a?.id)) ? 0 : 1
        const bSelected = selected.has(String(b?.id)) ? 0 : 1
        return aSelected - bSelected
      }),
    )
  }, [draftSelectedIds, accounts])

  const applyValue = useCallback(() => {
    onApply({
      id,
      newGroupData: {
        name: draftGroupName,
        account_ids: draftSelectedIds,
      },
    })

    setDraftGroupName('')
    setDraftSelectedIds([])
    popoverState.close()
  }, [draftGroupName, draftSelectedIds, onApply, popoverState])

  return isDemoMode ? (
    <Tooltip title={t('statistics::tooltips.not_available_for_demo')}>
      <ButtonComponent />
    </Tooltip>
  ) : (
    <>
      <div {...bindTrigger(popoverState)}>
        <ButtonComponent />
      </div>
      <Popover
        {...bindPopover(popoverState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 8,
          horizontal: 'left',
        }}
      >
        <Dropdown className={styles['group-manager__dropdown']}>
          <DropdownItem noHover>
            <TextInput
              className={styles['group-manager__input']}
              value={draftGroupName}
              onChange={(e) => setDraftGroupName(() => e.target.value)}
              fullWidth
              variant={'outlined'}
              label={t('statistics::table.groups_manager.group_name_placeholder')}
            />
          </DropdownItem>

          <div className={styles['group-manager__list']}>
            {normalizedAccounts.map((acc) => (
              <DropdownItem
                key={`group-manager-account-${acc.id}`}
                noHover
              >
                <ControlLabel
                  onChange={() => toggleAccount(acc.id)}
                  checked={draftSelectedIds.includes(acc.id)}
                  value={acc.id}
                  name={'groupAccounts'}
                  control={<Checkbox />}
                  className={styles['group-manager__control-label']}
                  label={
                    <Grid
                      wrap={'nowrap'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      gap={8}
                      className={styles['group-manager__accounts-row']}
                    >
                      <Col flexGrow={1}>
                        <Typography
                          variant={'Body-Forms-15'}
                          component={'div'}
                          className={styles['group-manager__account-label']}
                        >
                          {acc.login}
                        </Typography>
                      </Col>
                      <Col
                        flexGrow={1}
                        className={styles['group-manager__icon-col']}
                      >
                        <NetworkIcon
                          name={networkIconNamesMap[acc.network] || acc.network}
                          size={'m'}
                          width={32}
                          rounded
                        />
                      </Col>
                    </Grid>
                  }
                />
              </DropdownItem>
            ))}
          </div>

          <ActionButtons
            disabled={draftGroupName.length === 0 || draftSelectedIds.length === 0}
            showActionButtons
            dropFilter={dropSelection}
            applyValue={applyValue}
          />
        </Dropdown>
      </Popover>
    </>
  )
}

export default GroupManager

GroupManager.propTypes = {
  ButtonComponent: PropTypes.elementType,
  id: PropTypes.number,
  name: PropTypes.string,
  accounts: PropTypes.array,
  selectedAccountIds: PropTypes.array,
  onApply: PropTypes.func,
  onReset: PropTypes.func,
  isDemoMode: PropTypes.bool,
}
